import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  DollarSign,
  Flame,
  Gem,
  Lock,
  RefreshCw,
  Wrench,
  Zap,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

const CONVERSION_DETAILS = {
  cash: {
    title: "Liquid Funds",
    icon: DollarSign,
    accent: "green",
  },
  turns: {
    title: "Energy Stims",
    icon: Zap,
    accent: "yellow",
  },
  heat: {
    title: "Clean Record",
    icon: Flame,
    accent: "red",
  },
};

const ACCENT_STYLES = {
  green: {
    border: "border-green-900/50",
    icon: "border-green-900 bg-green-900/20 text-green-500",
    reward: "text-green-500",
    button: "bg-green-600 hover:bg-green-500",
  },
  yellow: {
    border: "border-yellow-900/50",
    icon: "border-yellow-900 bg-yellow-900/20 text-yellow-500",
    reward: "text-yellow-500",
    button: "bg-yellow-600 hover:bg-yellow-500",
  },
  red: {
    border: "border-red-900/50",
    icon: "border-red-900 bg-red-900/20 text-red-500",
    reward: "text-red-500",
    button: "bg-red-600 hover:bg-red-500",
  },
};

export default function PremiumStore({ stats, fetchStats }) {
  const [activeTab, setActiveTab] = useState("buy");
  const [storeData, setStoreData] = useState({
    purchasingEnabled: false,
    packages: {},
    conversions: {},
  });
  const [loading, setLoading] = useState(true);
  const [processingType, setProcessingType] = useState(null);
  const [confirmingType, setConfirmingType] = useState(null);
  const { addNotification } = useNotify();

  useEffect(() => {
    callBackend("/credits/packages")
      .then(setStoreData)
      .catch((error) => {
        addNotification(error.message, "error");
      })
      .finally(() => setLoading(false));
  }, [addNotification]);

  const handleConvert = async (type) => {
    setProcessingType(type);
    try {
      const data = await callBackend("/credits/convert", {
        method: "POST",
        body: { type },
      });
      addNotification(data.message, "success");
      setConfirmingType(null);
      await fetchStats();
    } catch (error) {
      addNotification(error.message, "error");
    } finally {
      setProcessingType(null);
    }
  };

  const getUnavailableReason = (type, conversion) => {
    if ((stats?.credits ?? 0) < conversion.cost) {
      return "Not enough credits";
    }

    if (
      type === "turns" &&
      (stats?.turns ?? 0) + conversion.reward > conversion.maxBalance
    ) {
      return `Requires ${conversion.reward} empty turn slots`;
    }

    if (type === "heat" && (stats?.heat_level ?? 0) < conversion.reward) {
      return `Requires at least ${conversion.reward} Heat`;
    }

    return null;
  };

  if (loading) {
    return (
      <div className="p-10 text-center font-mono uppercase text-zinc-500">
        Loading credit exchange...
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-5xl animate-in border-2 border-indigo-900 bg-zinc-950 font-mono shadow-2xl fade-in duration-500">
      <div className="flex flex-col justify-between gap-5 border-b border-indigo-900 bg-indigo-900/20 p-6 md:flex-row md:items-center md:p-8">
        <div>
          <h2 className="flex items-center gap-3 text-3xl font-black uppercase italic tracking-tighter text-white md:text-4xl">
            <Gem size={32} className="text-indigo-400" />
            Credit Exchange
          </h2>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-indigo-400">
            Premium currency and account services
          </p>
        </div>
        <div className="text-left md:text-right">
          <p className="text-[10px] font-black uppercase text-zinc-500">
            Your Balance
          </p>
          <p className="flex items-center gap-1 text-3xl font-black text-indigo-400 md:justify-end">
            {stats?.credits ?? 0} <Gem size={20} />
          </p>
        </div>
      </div>

      <div className="border-b border-amber-800 bg-amber-950/40 p-4">
        <div className="flex items-start gap-3 text-amber-300">
          <Wrench size={20} className="mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-black uppercase">Under Construction</p>
            <p className="mt-1 text-[10px] font-bold uppercase leading-relaxed text-amber-200/70">
              Buying credits is not available yet. No real-money payments can
              be started. Existing credit balances can still be redeemed.
            </p>
          </div>
        </div>
      </div>

      <div className="flex border-b border-zinc-800 bg-black">
        <button
          type="button"
          onClick={() => setActiveTab("buy")}
          className={`flex-1 py-4 text-xs font-black uppercase italic transition-all ${
            activeTab === "buy"
              ? "bg-indigo-600 text-white"
              : "text-zinc-500 hover:bg-zinc-900"
          }`}
        >
          Credit Packs
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("redeem")}
          className={`flex-1 py-4 text-xs font-black uppercase italic transition-all ${
            activeTab === "redeem"
              ? "bg-indigo-600 text-white"
              : "text-zinc-500 hover:bg-zinc-900"
          }`}
        >
          Redeem Credits
        </button>
      </div>

      <div className="p-5 md:p-8">
        {activeTab === "buy" && (
          <div>
            <div className="mb-6 flex items-start gap-3 border border-zinc-800 bg-black/50 p-4 text-zinc-400">
              <Lock size={18} className="mt-0.5 shrink-0" />
              <p className="text-[10px] font-bold uppercase leading-relaxed">
                These packs are shown as a preview only. Checkout will remain
                locked until the credit system is ready for release.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {Object.entries(storeData.packages).map(([key, creditPackage]) => (
                <div
                  key={key}
                  className="flex flex-col items-center border border-indigo-900/50 bg-black p-6 text-center opacity-70"
                >
                  <Gem size={48} className="mb-4 text-indigo-500" />
                  <h3 className="text-xl font-black uppercase italic text-white">
                    {creditPackage.name}
                  </h3>
                  <p className="my-4 text-2xl font-black text-indigo-400">
                    {creditPackage.amount.toLocaleString()} Credits
                  </p>
                  <button
                    type="button"
                    disabled
                    className="mt-auto flex w-full cursor-not-allowed items-center justify-center gap-2 bg-zinc-800 py-3 text-xs font-black uppercase text-zinc-500"
                  >
                    <Lock size={14} /> Coming Later
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "redeem" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {Object.entries(storeData.conversions).map(([type, conversion]) => {
              const details = CONVERSION_DETAILS[type];
              if (!details) return null;

              const Icon = details.icon;
              const styles = ACCENT_STYLES[details.accent];
              const unavailableReason = getUnavailableReason(type, conversion);
              const isConfirming = confirmingType === type;
              const isProcessing = processingType === type;

              return (
                <div
                  key={type}
                  className={`flex flex-col justify-between border bg-black p-6 ${styles.border}`}
                >
                  <div>
                    <div className="mb-5 flex items-center gap-4">
                      <div className={`border p-4 ${styles.icon}`}>
                        <Icon size={30} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black uppercase italic text-white">
                          {details.title}
                        </h3>
                        <p className="mt-1 text-[9px] font-bold uppercase leading-relaxed text-zinc-500">
                          {conversion.description}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4 flex items-center justify-between border border-zinc-800 bg-zinc-900 p-3">
                      <span className="flex items-center gap-1 font-black text-indigo-400">
                        <Gem size={14} /> {conversion.cost}
                      </span>
                      <RefreshCw size={14} className="text-zinc-600" />
                      <span className={`text-right text-sm font-black ${styles.reward}`}>
                        {conversion.name}
                      </span>
                    </div>
                  </div>

                  {isConfirming ? (
                    <div className="border border-amber-800 bg-amber-950/30 p-3">
                      <p className="mb-3 flex items-start gap-2 text-[10px] font-bold uppercase leading-relaxed text-amber-300">
                        <AlertTriangle size={14} className="shrink-0" />
                        Spend {conversion.cost} credits for {conversion.name}?
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirmingType(null)}
                          disabled={isProcessing}
                          className="bg-zinc-800 py-2 text-[10px] font-black uppercase text-zinc-300 hover:bg-zinc-700"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConvert(type)}
                          disabled={isProcessing}
                          className={`${styles.button} py-2 text-[10px] font-black uppercase text-black disabled:bg-zinc-800 disabled:text-zinc-600`}
                        >
                          {isProcessing ? "Processing..." : "Confirm"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        onClick={() => setConfirmingType(type)}
                        disabled={Boolean(unavailableReason) || processingType !== null}
                        className={`w-full py-3 text-xs font-black uppercase text-black transition-all disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-600 ${styles.button}`}
                      >
                        Redeem
                      </button>
                      {unavailableReason && (
                        <p className="mt-2 text-center text-[9px] font-bold uppercase text-zinc-600">
                          {unavailableReason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
